// What happened around a finding that met a standard, in a few plain
// sentences written by the chat's model (lib/findingSummary.js). Where no
// model is set up for this tenant, or the call fails, `fallback` shows instead.
import { useEffect, useState } from "react";
import { keptSummary, summarize } from "../../lib/findingSummary.js";
import { llmErrorCopy } from "../../lib/llmErrorCopy.js";
import { openPreviewModel } from "../../lib/standardPreview.js";
import { StoryLoading } from "./FindingStory.jsx";

// One model for the page, opened at the first summary asked for.
let opening = null;
function openModel() {
  if (!opening) opening = openPreviewModel().catch((e) => { opening = null; throw e; });
  return opening;
}
export function _resetSummaryModelForTests() {
  opening = null;
}

// The inline Markdown a model writes even when asked for plain text: `code`
// for a file or command, **bold** and *italic*. Anything else reads as typed.
const INLINE = /(`[^`\n]+`|\*\*[^*\n]+\*\*|\*[^*\s][^*\n]*\*)/g;
export function inlineMarkdown(text) {
  return String(text).split(INLINE).map((part, i) => {
    if (i % 2 === 0) return part;
    if (part.startsWith("`")) return <code key={i} className="rounded-[2px] bg-[color:var(--control-bg)] px-[3px] font-mono text-[0.88em]">{part.slice(1, -1)}</code>;
    if (part.startsWith("**")) return <strong key={i} className="font-medium">{part.slice(2, -2)}</strong>;
    return <em key={i}>{part.slice(1, -1)}</em>;
  });
}

// Set off from the card and timeline under it by a rule in the finding's
// tone, at a width a paragraph reads at.
const CALLOUT = "max-w-[120ch] border-l-2 border-[color:var(--danger,#d4503e)] py-[1px] pl-[10px]";

// `summaryKey`: the finding and the verdict it is written from. `input`: the
// request, from summaryInput. `fallback`: what shows where no summary can be
// written.
export default function FindingSummary({ summaryKey, input, fallback = null }) {
  const [state, setState] = useState(() => {
    const text = keptSummary(summaryKey);
    return text ? { text } : { busy: true };
  });
  useEffect(() => {
    const text = keptSummary(summaryKey);
    if (text) { setState({ text }); return undefined; }
    let live = true;
    setState({ busy: true });
    openModel().then(
      (model) => summarize({ key: summaryKey, input, complete: model.complete }).then(
        (answer) => { if (live) setState({ text: answer.text }); },
        (e) => { if (live) setState({ error: llmErrorCopy(e?.serverMessage || e?.message || e).message }); },
      ),
      () => { if (live) setState({ off: true }); },
    );
    return () => { live = false; };
  }, [summaryKey, input]);

  if (state.busy) return <span data-finding-summary="loading"><StoryLoading label="Writing a summary" /></span>;
  if (state.off || state.error) {
    return fallback ? <div data-finding-summary="fallback" className={CALLOUT} title={state.error ? `Couldn\u2019t write a summary. ${state.error}` : undefined}>{fallback}</div> : null;
  }
  return (
    <p data-finding-summary="" title="Written by the chat's model from this trace's record" className={`m-0 text-[12.5px] leading-[1.6] text-[color:var(--ink)] [overflow-wrap:anywhere] ${CALLOUT}`}>
      {inlineMarkdown(state.text)}
    </p>
  );
}
